import type { Rect } from './overlays';

/**
 * Words at each ring (founder, 2026-10-15): where the line is not heard and the demo plays itself
 * (Watch muted), a ring lit with no words beside it reads as a blink "without any explanation".
 * Each ring gets its phrase in a small label, one at a time. Click-through has callouts instead
 * (callout.ts): the viewer reads at their own pace.
 */

/** A phrase as a label: first letter up, no full stop added, its own trailing one dropped. */
export function labelText(phrase: string): string {
  const t = phrase.trim().replace(/[.,;:]+$/, '');
  return t ? t[0]!.toUpperCase() + t.slice(1) : '';
}

/**
 * Where a ring's label goes, in the same px as `ring`: below the ring, else above, else beside
 * it; inside `area` (the picture, or the story's window, off the chrome bar) and off every rect
 * in `avoid` (the tooltip, the guide card). Left-aligned with the ring, else centred on it, else
 * right-aligned. When nothing is clear, the place covering least, then inside the ring's foot.
 */
export function placeLabel(
  ring: Rect,
  size: { w: number; h: number },
  area: Rect,
  avoid: readonly Rect[],
  gap = 8,
): { x: number; y: number } {
  const { w, h } = size;
  const xs = [ring.x, ring.x + ring.w / 2 - w / 2, ring.x + ring.w - w];
  const ys = [ring.y, ring.y + ring.h / 2 - h / 2, ring.y + ring.h - h];
  const below = ring.y + ring.h + gap;
  const above = ring.y - gap - h;
  const cands: { x: number; y: number }[] = [];
  for (const x of xs) cands.push({ x, y: below });
  for (const x of xs) cands.push({ x, y: above });
  for (const y of ys) cands.push({ x: ring.x + ring.w + gap, y });
  for (const y of ys) cands.push({ x: ring.x - gap - w, y });
  const clampX = (x: number) => Math.max(area.x, Math.min(x, area.x + area.w - w));
  const clampY = (y: number) => Math.max(area.y, Math.min(y, area.y + area.h - h));
  let best: { x: number; y: number } | null = null;
  let bestCost = Infinity;
  for (const c of cands) {
    // Outside the area along the ring's own axis is not allowed (it would cover the ring once
    // clamped); along the other axis it is clamped in.
    const sideways = c.y !== below && c.y !== above;
    const inArea = sideways
      ? c.x >= area.x - 0.5 && c.x + w <= area.x + area.w + 0.5
      : c.y >= area.y - 0.5 && c.y + h <= area.y + area.h + 0.5;
    if (!inArea) continue;
    const p = { x: clampX(c.x), y: clampY(c.y) };
    const r = { x: p.x, y: p.y, w, h };
    const cost = overlap(r, avoid) + overlap(r, [ring]) * 0.5;
    if (cost < 0.5) return p;
    if (cost < bestCost) {
      best = p;
      bestCost = cost;
    }
  }
  // Nothing outside the ring is clear: inside its foot, where a card's own text is least often.
  const inside = { x: clampX(ring.x + 10), y: clampY(ring.y + ring.h - h - 10) };
  const insideCost = overlap({ ...inside, w, h }, avoid);
  return best && bestCost <= insideCost ? best : inside;
}

/** How much of `r` the rects in `over` cover, in px². */
export function overlap(r: Rect, over: readonly Rect[]): number {
  let a = 0;
  for (const o of over) {
    const w = Math.min(r.x + r.w, o.x + o.w) - Math.max(r.x, o.x);
    const h = Math.min(r.y + r.h, o.y + o.h) - Math.max(r.y, o.y);
    if (w > 0 && h > 0) a += w * h;
  }
  return a;
}

/**
 * Where `phrase` is in `text`, case-insensitive, its own trailing punctuation aside: [start,
 * end), or null. A callout sets its ring's phrase in emphasis in its sentence (callout.ts).
 */
export function phraseSpan(text: string, phrase: string): [number, number] | null {
  const p = phrase
    .trim()
    .replace(/[.,;:!?]+$/, '')
    .toLowerCase();
  if (!p) return null;
  const i = text.toLowerCase().indexOf(p);
  return i < 0 ? null : [i, i + p.length];
}
